"use client";

import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import log from "logbeacon";
import { safeStringifyError } from "utils";
import { invalidateChatDetailQueries } from "@/src/utils/chatDetailInvalidateQueries";
import { trackPutEdToWorkTap } from "@/src/hooks/onboarding/onboardingAnalytics";
import { openOnboardingPaywall } from "@/src/components/ChatOnboarding/openOnboardingPaywall";
import type { SaveGoalArgs } from "@/src/components/Chat/InteractiveTool/types";
import type { ConfirmSaveGoalResponse } from "@/src/features/api/ChatApi";
import { QUERY_KEYS } from "@/src/config/constant";
import { useConfirmSaveGoal } from "@/src/hooks/onboarding/useConfirmSaveGoal";
import { useMemberUser } from "@/src/hooks/useMemberUser";
import { useIsPc } from "@/src/hooks/useIsPc";
import { goalQueryKeys } from "@/src/hooks/goal/queryKeys";
import { planQueryKeys } from "@/src/hooks/plan/planQueryKeys";
import { useActivateGoal } from "./useActivateGoal";
import { resolveGoalIdForActivate } from "./resolveGoalIdForActivate";

export type GoalActivateEntry = "chat_active_my_plan" | "plan_planned_activate";

export type GoalActivateRunResult =
  | { outcome: "dismissed" }
  | { outcome: "missing_goal_id" }
  | { outcome: "activated"; goalId: string }
  | {
      outcome: "confirmed";
      goalId: string;
      status?: ConfirmSaveGoalResponse["status"];
    }
  | { outcome: "error"; error: unknown };

export type RunGoalActivateParams = {
  entry: GoalActivateEntry;
  goalId?: string;
  saveGoalArgs?: SaveGoalArgs;
  /** Chat 卡片是否已有 tool result（已 confirm-save） */
  hasConfirmSaveResult?: boolean;
  /** Chat 会话 id；confirm-save 成功后刷新详情（Active my plan） */
  chatId?: string;
};

function scheduleGoalPlanInvalidation(
  queryClient: ReturnType<typeof useQueryClient>,
): void {
  setTimeout(() => {
    void queryClient.invalidateQueries({ queryKey: goalQueryKeys.all });
    void queryClient.invalidateQueries({ queryKey: goalQueryKeys.stats() });
    queryClient.removeQueries({ queryKey: planQueryKeys.diagnosisGoals() });
    void queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.MEMBER_USE_PLAN],
    });
  }, 1000);
}

function syncChatDetailAfterActivate(
  queryClient: ReturnType<typeof useQueryClient>,
  chatId?: string,
): void {
  const trimmed = chatId?.trim();
  if (trimmed) {
    invalidateChatDetailQueries(queryClient, trimmed);
  }
}

async function activatePlannedGoal(
  activateGoal: ReturnType<typeof useActivateGoal>["mutateAsync"],
  goalId: string,
): Promise<GoalActivateRunResult> {
  try {
    await activateGoal(goalId);
    return { outcome: "activated", goalId };
  } catch (error) {
    const message = safeStringifyError(error);
    if (message.includes("409") || message.includes("PLANNED")) {
      log.info("[GoalActivateGate] activate skipped — already active", {
        goalId,
      });
      return { outcome: "activated", goalId };
    }
    log.error("[GoalActivateGate] activate failed", message);
    return { outcome: "error", error };
  }
}

/**
 * US2 goal 激活 Paywall gate — Chat「Active my plan」与 Plan PLANNED 激活点共用。
 */
export function useGoalActivatePaywallGate() {
  const [isPC] = useIsPc();
  const queryClient = useQueryClient();
  const { data: memberPlanInfo } = useMemberUser();
  const isMember = memberPlanInfo?.isMember === true;
  const { mutateAsync: confirmSaveGoal, isPending: isConfirmSaving } =
    useConfirmSaveGoal();
  const { mutateAsync: activateGoal, isPending: isActivating } =
    useActivateGoal();

  const openGoalActivatePaywall = useCallback(async () => {
    return openOnboardingPaywall(isPC, {
      paywallVariant: "goal_activate_second",
    });
  }, [isPC]);

  const requirePaywallIfNonMember = useCallback(async (): Promise<boolean> => {
    if (isMember) {
      return true;
    }

    const paywallOutcome = await openGoalActivatePaywall();

    if (paywallOutcome !== "purchased") {
      return false;
    }

    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.MEMBER_USE_PLAN],
    });
    return true;
  }, [isMember, openGoalActivatePaywall, queryClient]);

  const resolveChatShouldActivate = useCallback(async (): Promise<boolean> => {
    if (isMember) {
      return true;
    }

    const paywallOutcome = await openGoalActivatePaywall();

    if (paywallOutcome === "purchased") {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MEMBER_USE_PLAN],
      });
      return true;
    }

    return false;
  }, [isMember, openGoalActivatePaywall, queryClient]);

  const runActivate = useCallback(
    async (params: RunGoalActivateParams): Promise<GoalActivateRunResult> => {
      const resolvedGoalId = resolveGoalIdForActivate(
        params.saveGoalArgs,
        params.goalId,
      );

      if (params.entry === "plan_planned_activate") {
        trackPutEdToWorkTap("plan");
        const paid = await requirePaywallIfNonMember();
        if (!paid) {
          return { outcome: "dismissed" };
        }

        if (!resolvedGoalId) {
          log.warn("[GoalActivateGate] plan activate missing goalId");
          return { outcome: "missing_goal_id" };
        }

        const result = await activatePlannedGoal(activateGoal, resolvedGoalId);
        if (result.outcome === "activated") {
          scheduleGoalPlanInvalidation(queryClient);
        }
        return result;
      }

      // chat_active_my_plan — paywall gates activate only; confirm-save runs after paywall closes
      // confirm-save 允许无 goalId（创建 ACTIVE）；已 confirm 过的二次 activate 仍必须有 goalId
      const shouldActivate = await resolveChatShouldActivate();

      if (params.hasConfirmSaveResult) {
        if (!shouldActivate) {
          return { outcome: "dismissed" };
        }

        if (!resolvedGoalId) {
          log.warn(
            "[GoalActivateGate] activate skipped — missing goalId (has confirm result)",
          );
          return { outcome: "missing_goal_id" };
        }

        const activateResult = await activatePlannedGoal(
          activateGoal,
          resolvedGoalId,
        );
        if (activateResult.outcome === "activated") {
          scheduleGoalPlanInvalidation(queryClient);
          const result = {
            outcome: "confirmed" as const,
            goalId: resolvedGoalId,
            status: "ACTIVE" as const,
          };
          syncChatDetailAfterActivate(queryClient, params.chatId);
          return result;
        }

        return activateResult;
      }

      let confirmResponse: ConfirmSaveGoalResponse | undefined;

      if (params.saveGoalArgs) {
        try {
          confirmResponse = await confirmSaveGoal({
            ...params.saveGoalArgs,
            ...(resolvedGoalId
              ? { goalId: resolvedGoalId }
              : { goalId: undefined }),
          });
        } catch (error) {
          log.error(
            "[GoalActivateGate] confirm-save failed",
            safeStringifyError(error),
          );
          return { outcome: "error", error };
        }

        if (!confirmResponse?.goalId) {
          return { outcome: "error", error: confirmResponse };
        }

        scheduleGoalPlanInvalidation(queryClient);
      }

      const goalId = confirmResponse?.goalId ?? resolvedGoalId;
      const status = confirmResponse?.status;

      if (!shouldActivate || status === "ACTIVE") {
        const result = {
          outcome: "confirmed" as const,
          goalId: goalId ?? "",
          status,
        };
        syncChatDetailAfterActivate(queryClient, params.chatId);
        return result;
      }

      if (!goalId) {
        log.warn(
          "[GoalActivateGate] activate skipped — missing goalId after confirm-save",
        );
        return { outcome: "missing_goal_id" };
      }

      const activateResult = await activatePlannedGoal(activateGoal, goalId);
      if (activateResult.outcome === "activated") {
        scheduleGoalPlanInvalidation(queryClient);
      }

      const result = {
        outcome: "confirmed" as const,
        goalId,
        status:
          activateResult.outcome === "activated"
            ? ("ACTIVE" as const)
            : (status ?? "PLANNED"),
      };
      syncChatDetailAfterActivate(queryClient, params.chatId);
      return result;
    },
    [
      activateGoal,
      confirmSaveGoal,
      isMember,
      queryClient,
      requirePaywallIfNonMember,
      resolveChatShouldActivate,
    ],
  );

  return {
    runActivate,
    isActivating: isConfirmSaving || isActivating,
    isMember,
  };
}
